import React, { useRef } from "react";
import { useResizeObserver } from "usehooks-ts";

import { ButtonStandard, Text } from "@performyard/py-components";

import VerticalExpander from "../../../../components/AnimationWrappers/VerticalExpander";

// ~3 lines of paragraph-3 text — matches the shared ExpandableText's default collapse point.
const COLLAPSE_HEIGHT = 48;

type DescriptionClampProps = {
	content: string;
	isExpanded: boolean;
	onToggle: () => void;
};

// A controlled sibling of the shared ExpandableText component. ExpandableText owns its expanded
// flag via internal useState, which would reset every time applyTransaction recreates this row's
// renderer — so the flag is passed in from the page-level hook here instead.
export const DescriptionClamp = ({
	content,
	isExpanded,
	onToggle,
}: DescriptionClampProps): React.ReactElement | null => {
	const contentRef = useRef<HTMLDivElement | null>(null);
	const { height: contentHeight = 0 } = useResizeObserver({ ref: contentRef as React.RefObject<HTMLDivElement> });

	if (!content?.trim()) return null;

	const showReadMore = contentHeight > COLLAPSE_HEIGHT;

	return (
		<div>
			<VerticalExpander isExpanded={isExpanded} closedHeight={COLLAPSE_HEIGHT}>
				<div ref={contentRef}>
					<Text size="paragraph-3" weight="regular" dangerouslySetClassName="whitespace-pre-wrap">
						{content}
					</Text>
				</div>
			</VerticalExpander>
			{showReadMore && (
				<div className="w-fit pt-2">
					<ButtonStandard
						variant="micro"
						color="neutral"
						onClick={onToggle}
						dangerouslySetClassName="bg-neutral-2 hover:bg-neutral-5 active:bg-neutral-8 active:text-neutral-0 text-neutral-9"
					>
						{isExpanded ? "Read Less" : "Read More"}
					</ButtonStandard>
				</div>
			)}
		</div>
	);
};
